// ---------------------------------------------------------------------------
// Canonical currency data shared across admin + customer apps.
//
// Curated set of popular worldwide + Caribbean currencies. This is the single
// source of truth for the settings currency picker and for symbol lookup used
// by every money-render site (via each app's currencyUtils delegate).
// ---------------------------------------------------------------------------

export interface CurrencyInfo {
  /** ISO-4217 code, e.g. 'USD'. */
  code: string;
  /** Human-readable name, e.g. 'US Dollar'. */
  name: string;
  /** Display symbol, e.g. '$', 'J$', '£'. */
  symbol: string;
}

// Order: popular worldwide first, then Caribbean/regional. JMD/USD/GBP are the
// primary targets and appear near the top for quick selection.
export const CURRENCIES: CurrencyInfo[] = [
  { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: 'CN¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'Dh' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'Mex$' },
  // Caribbean & nearby
  { code: 'TTD', name: 'Trinidad & Tobago Dollar', symbol: 'TT$' },
  { code: 'BBD', name: 'Barbadian Dollar', symbol: 'Bds$' },
  { code: 'XCD', name: 'East Caribbean Dollar', symbol: 'EC$' },
  { code: 'GYD', name: 'Guyanese Dollar', symbol: 'G$' },
  { code: 'KYD', name: 'Cayman Islands Dollar', symbol: 'CI$' },
  { code: 'BSD', name: 'Bahamian Dollar', symbol: 'B$' },
  { code: 'BZD', name: 'Belize Dollar', symbol: 'BZ$' },
  { code: 'BMD', name: 'Bermudian Dollar', symbol: 'BD$' },
  { code: 'HTG', name: 'Haitian Gourde', symbol: 'G' },
  { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$' },
  { code: 'ANG', name: 'Netherlands Antillean Guilder', symbol: 'ƒ' },
  { code: 'AWG', name: 'Aruban Florin', symbol: 'ƒ' },
];

export const CURRENCY_SYMBOLS: Record<string, string> = CURRENCIES.reduce(
  (acc, c) => {
    acc[c.code] = c.symbol;
    return acc;
  },
  {} as Record<string, string>
);

export const DEFAULT_CURRENCY_SYMBOL = '$';

/**
 * Returns the display symbol for a currency code, defaulting to '$' for unknown
 * codes. Case-insensitive.
 */
export const getCurrencySymbol = (currency?: string | null): string => {
  if (!currency) return DEFAULT_CURRENCY_SYMBOL;
  return CURRENCY_SYMBOLS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_SYMBOL;
};

/** Picker options: `{ value: code, label: 'Name (CODE)' }`. */
export const getCurrencyOptions = (): { value: string; label: string }[] =>
  CURRENCIES.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));
